import {useEffect} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {
  Button,
  Spin,
  Form,
  InputNumber,
  Switch,
  Card,
  Divider,
  Tabs,
  Modal,
} from 'antd';
import {toast} from 'react-toastify';
import {GET_SIMULATOR_CONFIG, UPDATE_SIMULATOR} from './resolvers';
import styles from './simulator.module.scss';

const renderCurrencyInput = (name, label, icon) => (
  <Form.Item
    name={name}
    label={
      <span style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
        {icon} {label}
      </span>
    }
    rules={[{required: true}]}>
    <InputNumber
      formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
      parser={value => value.replace(/\$\s?|(,*)/g, '')}
      className={styles['input-number']}
      min={0}
      placeholder="0.00"
      style={{width: '100%'}}
    />
  </Form.Item>
);

export const SimulatorConfig = () => {
  const [form] = Form.useForm();

  const {data, loading, refetch} = useQuery(GET_SIMULATOR_CONFIG, {
    fetchPolicy: 'network-only',
    onError: error => toast.error(error.message),
  });

  const [updateSimulator, {loading: updating}] = useMutation(UPDATE_SIMULATOR, {
    onError: error => toast.error(error.message),
    onCompleted: data => {
      if (data?.updateSimulator?.__typename === 'Error') {
        toast.error(data.updateSimulator.message);
      } else {
        toast.success('Simulator configuration updated successfully');
        refetch();
      }
    },
  });

  useEffect(() => {
    if (data?.getSimulator?.data) {
      const config = data.getSimulator.data;
      form.setFieldsValue({
        active: config.active,
        free: {
          ...config.free,
        },
        premium: {
          ...config.premium,
        },
      });
    }
  }, [data, form]);

  const onFinish = values => {
    Modal.confirm({
      title: 'Update Configuration',
      content: 'Are you sure you want to update the simulator configuration?',
      onOk() {
        // Tabs only mount the active pane, and antd collects submitted values
        // from mounted fields only. `forceRender` on the tab items keeps both
        // populated, but fall back to the fetched config anyway so a missing
        // pane degrades to "leave it unchanged" instead of throwing.
        const config = data?.getSimulator?.data;
        const free = values.free ?? config?.free ?? {};
        const premium = values.premium ?? config?.premium ?? {};

        // Construct the input object to match the exact structure required
        const input = {
          active: values.active,
          free: {
            resetLimit: free.resetLimit,
            resetFee: {
              XOF: free.resetFee?.XOF,
              XAF: free.resetFee?.XAF,
              USD: free.resetFee?.USD,
              EUR: free.resetFee?.EUR,
            },
            maxWalletValue: free.maxWalletValue,
            fundingValue: free.fundingValue,
            fundingFee: {
              XOF: free.fundingFee?.XOF,
              XAF: free.fundingFee?.XAF,
              USD: free.fundingFee?.USD,
              EUR: free.fundingFee?.EUR,
            },
            duration: free.duration,
            baseWalletValue: free.baseWalletValue,
            pricing: {
              yearly: {
                USD: data?.getSimulator?.data?.free?.pricing?.yearly?.USD,
                XOF: data?.getSimulator?.data?.free?.pricing?.yearly?.XOF,
                XAF: data?.getSimulator?.data?.free?.pricing?.yearly?.XAF,
                EUR: data?.getSimulator?.data?.free?.pricing?.yearly?.EUR,
              },
              monthly: {
                XOF: data?.getSimulator?.data?.free?.pricing?.monthly?.XOF,
                XAF: data?.getSimulator?.data?.free?.pricing?.monthly?.XAF,
                USD: data?.getSimulator?.data?.free?.pricing?.monthly?.USD,
                EUR: data?.getSimulator?.data?.free?.pricing?.monthly?.EUR,
              },
            },
          },
          premium: {
            resetLimit: premium.resetLimit,
            resetFee: {
              XOF: premium.resetFee?.XOF,
              XAF: premium.resetFee?.XAF,
              USD: premium.resetFee?.USD,
              EUR: premium.resetFee?.EUR,
            },
            maxWalletValue: premium.maxWalletValue,
            fundingValue: premium.fundingValue,
            fundingFee: {
              XOF: premium.fundingFee?.XOF,
              XAF: premium.fundingFee?.XAF,
              USD: premium.fundingFee?.USD,
              EUR: premium.fundingFee?.EUR,
            },
            duration: data?.getSimulator?.data?.premium?.duration,
            baseWalletValue: premium.baseWalletValue,
            pricing: {
              yearly: {
                USD: premium.pricing?.yearly?.USD,
                XOF: premium.pricing?.yearly?.XOF,
                XAF: premium.pricing?.yearly?.XAF,
                EUR: premium.pricing?.yearly?.EUR,
              },
              monthly: {
                XOF: premium.pricing?.monthly?.XOF,
                XAF: premium.pricing?.monthly?.XAF,
                USD: premium.pricing?.monthly?.USD,
                EUR: premium.pricing?.monthly?.EUR,
              },
            },
          },
        };

        updateSimulator({
          variables: {
            input,
          },
        });
      },
    });
  };

  if (loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: '50px'}}>
        <Spin size="large" />
      </div>
    );
  }

  const renderConfigFields = type => (
    <>
      <div className={styles['section-title']}>
        {type === 'free'
          ? 'Free Plan Configuration'
          : 'Premium Plan Configuration'}
      </div>

      {type !== 'free' && (
        <>
          <Divider orientation="left">PRICING (MONTHLY)</Divider>
          <div className={styles['pricing-section']}>
            {renderCurrencyInput(
              [type, 'pricing', 'monthly', 'XOF'],
              'XOF',
              '🇨🇮',
            )}
            {renderCurrencyInput(
              [type, 'pricing', 'monthly', 'XAF'],
              'XAF',
              '🇨🇲',
            )}
            {renderCurrencyInput(
              [type, 'pricing', 'monthly', 'USD'],
              'USD',
              '🇺🇸',
            )}
            {renderCurrencyInput(
              [type, 'pricing', 'monthly', 'EUR'],
              'EUR',
              '🇪🇺',
            )}
          </div>

          <Divider orientation="left">PRICING (YEARLY)</Divider>
          <div className={styles['pricing-section']}>
            {renderCurrencyInput(
              [type, 'pricing', 'yearly', 'XOF'],
              'XOF',
              '🇨🇮',
            )}
            {renderCurrencyInput(
              [type, 'pricing', 'yearly', 'XAF'],
              'XAF',
              '🇨🇲',
            )}
            {renderCurrencyInput(
              [type, 'pricing', 'yearly', 'USD'],
              'USD',
              '🇺🇸',
            )}
            {renderCurrencyInput(
              [type, 'pricing', 'yearly', 'EUR'],
              'EUR',
              '🇪🇺',
            )}
          </div>
        </>
      )}

      <Divider orientation="left">WALLET SETTINGS</Divider>
      <div className={styles['config-row']}>
        <Form.Item
          name={[type, 'baseWalletValue']}
          label="Base Wallet Value"
          tooltip="Starting Wallet Balance"
          rules={[{required: true}]}>
          <InputNumber
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            className={styles['input-number']}
            min={0}
          />
        </Form.Item>
        <Form.Item
          name={[type, 'maxWalletValue']}
          label="Maximum Wallet Value"
          tooltip="Maximum Wallet Balance"
          rules={[{required: true}]}>
          <InputNumber
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            className={styles['input-number']}
            min={0}
          />
        </Form.Item>
        {type !== 'premium' && (
          <Form.Item
            name={[type, 'duration']}
            label="Duration (months)"
            rules={[{required: true}]}>
            <InputNumber className={styles['input-number']} min={0} />
          </Form.Item>
        )}
      </div>

      <Divider orientation="left">VIRTUAL FUNDING FEE SETTINGS</Divider>

      <div className={styles['pricing-section']}>
        {renderCurrencyInput([type, 'fundingFee', 'XOF'], 'XOF', '🇨🇮')}
        {renderCurrencyInput([type, 'fundingFee', 'XAF'], 'XAF', '🇨🇲')}
        {renderCurrencyInput([type, 'fundingFee', 'USD'], 'USD', '🇺🇸')}
        {renderCurrencyInput([type, 'fundingFee', 'EUR'], 'EUR', '🇪🇺')}
      </div>

      <div className={styles['config-row']}>
        <Form.Item
          name={[type, 'fundingValue']}
          label="Virtual Funding Value (XOF Amount received)"
          tooltip="The amount in XOF that the user receives in their wallet"
          rules={[{required: true}]}>
          <InputNumber
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            className={styles['input-number']}
            min={0}
          />
        </Form.Item>
      </div>

      <Divider orientation="left">RESET SETTINGS</Divider>
      <div className={styles['config-row']}>
        <Form.Item
          name={[type, 'resetLimit']}
          label="Reset Limit"
          tooltip="Number of times a user can reset their simulator per month"
          rules={[{required: true}]}>
          <InputNumber
            formatter={value =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
            className={styles['input-number']}
            min={0}
          />
        </Form.Item>
      </div>

      <div className={styles['section-title']} style={{fontSize: '14px'}}>
        Reset Fees (Fee charged when users exceed their monthly reset limit)
      </div>
      <div className={styles['pricing-section']}>
        {renderCurrencyInput([type, 'resetFee', 'XOF'], 'XOF', '🇨🇮')}
        {renderCurrencyInput([type, 'resetFee', 'XAF'], 'XAF', '🇨🇲')}
        {renderCurrencyInput([type, 'resetFee', 'USD'], 'USD', '🇺🇸')}
        {renderCurrencyInput([type, 'resetFee', 'EUR'], 'EUR', '🇪🇺')}
      </div>
    </>
  );

  return (
    <div className={styles['form-container']}>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{active: false}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}>
          <h2 className={styles.title}>Simulator Configuration</h2>
        </div>
        <Form.Item
          name="active"
          label="Simulator Active"
          valuePropName="checked"
          style={{margin: 0}}>
          <Switch />
        </Form.Item>

        <Tabs
          defaultActiveKey="free"
          type="card"
          items={[
            {
              key: 'free',
              label: 'Free Plan',
              // Both panes must mount up front: antd only collects submitted
              // values from mounted fields, so a lazily-rendered pane would
              // submit as undefined if the user never opened that tab.
              forceRender: true,
              children: (
                <Card bordered={false}>{renderConfigFields('free')}</Card>
              ),
            },
            {
              key: 'premium',
              label: 'Premium Plan',
              forceRender: true,
              children: (
                <Card bordered={false}>{renderConfigFields('premium')}</Card>
              ),
            },
          ]}
        />

        <div className={styles['submit-container']}>
          <Button
            type="primary"
            htmlType="submit"
            loading={updating}
            size="large">
            Update Configuration
          </Button>
        </div>
      </Form>
    </div>
  );
};
